import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { IPermissionScope } from 'src/app/auth/auth.model';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { State } from 'src/app/store';
import { userScopesSelector } from 'src/app/user/store/user.selectors';

@Injectable()
export class ScopeGuard implements CanActivateChild {
    public t: any = {};

    constructor(
        private store: Store<State>,
        private router: Router,
        private messageService: MessageService,
        private translateService: TranslateService,
    ) {
        this.translateService
            .get('common.errors')
            .pipe(tap((t) => (this.t = t)))
            .subscribe();
    }

    canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean> {
        // If no scopes required for this route, allow immediately
        if (!route?.data?.scopes) {
            return of(true);
        }

        return this.store.select(userScopesSelector).pipe(
            filter((scopes) => scopes !== undefined),
            take(1),
            map((scopes) => {
                // If user has no scopes but route requires them, deny access
                if (!scopes || scopes.length === 0) {
                    this.router.navigate(['/']);
                    this.messageService.error(this.t.notAllowed);
                    return false;
                }

                if (this.hasScope(scopes, route?.data?.scopes as IPermissionScope)) {
                    return true;
                }

                this.router.navigate(['/']);
                this.messageService.error(this.t.notAllowed);
                return false;
            }),
        );
    }

    /**
     * Check if user has a specific scope.
     * Converts readable IRoleScope to short-format for matching.
     *
     * @param userScopes - User's scopes from JWT token (short format)
     * @param required - Required scope (readable format)
     * @returns true if user has the required scope with sufficient access level
     */
    private hasScope(userScopes: IPermissionScope[], required: IPermissionScope): boolean {
        return userScopes.some((scope) => {
            // Match permission and resource
            const matchesPermissionAndResource = scope.permission === required.permission && scope.resource === required.resource;

            if (!matchesPermissionAndResource) {
                return false;
            }

            // Wildcard resourceId (*) grants access to all resources
            const matchesResourceId = scope.resourceId === '*' || required.resourceId === '*' || scope.resourceId === required.resourceId;

            if (!matchesResourceId) {
                return false;
            }

            if (required.accessLevel === 'read') {
                return scope.accessLevel === 'read' || scope.accessLevel === 'write';
            }

            return scope.accessLevel === 'write';
        });
    }
}
